import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Dropdown, Form, Header, Icon, Input, Message } from 'semantic-ui-react';
import { LoadingWrapper, ReactTable } from '../common';
import { fetchWithAuth, getCSRFToken, isICCSupport, isReadOnly } from '../auth';

const emptyRequirement = { teamTypeId: null, label: '', quantity: 1, deviceSubTypeIds: [], sortOrder: 1 };

const endpoint = (path) => `${process.env.REACT_APP_ICC_TOOL_API_HOST}${path}`;

const responseData = async response => {
    if (!response.ok) throw response;
    if (response.status === 204) return null;
    return response.json();
};

const requirementForm = requirement => ({
    teamTypeId: requirement.teamType?.id ?? requirement.teamTypeId ?? null,
    label: requirement.label || '',
    quantity: requirement.quantity ?? 1,
    deviceSubTypeIds: (requirement.deviceSubTypes || []).map(subtype => subtype.id),
    sortOrder: requirement.sortOrder ?? 1
});

const requirementToRow = (requirement, teamTypes, deviceSubTypes) => {
    const form = requirementForm(requirement);
    const teamType = teamTypes.find(type => type.id === form.teamTypeId) || requirement.teamType;
    const selectedSubtypes = deviceSubTypes.filter(subtype => form.deviceSubTypeIds.includes(subtype.id));
    return {
        ...form,
        id: requirement.id,
        teamTypeName: teamType?.name || '',
        deviceSubTypeNames: selectedSubtypes
            .map(subtype => `${subtype.deviceType?.name || 'Asset'} - ${subtype.name}`)
            .join(', ')
    };
};

const requirementPayload = requirement => ({
    teamTypeId: requirement.teamTypeId,
    label: requirement.label.trim(),
    quantity: Number(requirement.quantity),
    deviceSubTypeIds: requirement.deviceSubTypeIds,
    sortOrder: Number(requirement.sortOrder)
});

export const TeamAssetRequirementManager = () => {
    const [requirements, setRequirements] = useState([]);
    const [teamTypes, setTeamTypes] = useState([]);
    const [deviceSubTypes, setDeviceSubTypes] = useState([]);
    const [newRequirement, setNewRequirement] = useState(emptyRequirement);
    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState('');
    const [readOnly, setReadOnly] = useState(isReadOnly() || !isICCSupport());
    const [adding, setAdding] = useState(false);
    const [savingRequirementId, setSavingRequirementId] = useState(null);

    useEffect(() => {
        Promise.all([
            fetchWithAuth(endpoint('team-asset-requirements/'), { credentials: 'include' }).then(responseData),
            fetchWithAuth(endpoint('team-types/'), { credentials: 'include' }).then(responseData),
            fetchWithAuth(endpoint('devicesubtypes/'), { credentials: 'include' }).then(responseData)
        ])
            .then(([requirementData, typeData, subtypeData]) => {
                const loadedTeamTypes = typeData.results || typeData;
                const loadedSubtypes = subtypeData.results || subtypeData;
                setRequirements((requirementData.results || requirementData).map(requirement =>
                    requirementToRow(requirement, loadedTeamTypes, loadedSubtypes)
                ));
                setTeamTypes(loadedTeamTypes);
                setDeviceSubTypes(loadedSubtypes);
            })
            .catch(() => setError('Could not load team asset requirements.'))
            .finally(() => setLoaded(true));
    }, []);

    const handleWriteError = async response => {
        if (response.status === 403) {
            setReadOnly(true);
            setError('You do not have permission to edit team asset requirements. This page is now read-only.');
            return;
        }
        setError('Could not save team asset requirements.');
    };

    const updateRequirementField = useCallback((id, field, value) => {
        setRequirements(current => current.map(requirement => {
            if (requirement.id !== id) return requirement;
            const updated = { ...requirement, [field]: value };
            if (field === 'teamTypeId') {
                updated.teamTypeName = teamTypes.find(type => type.id === value)?.name || '';
            }
            if (field === 'deviceSubTypeIds') {
                updated.deviceSubTypeNames = deviceSubTypes
                    .filter(subtype => value.includes(subtype.id))
                    .map(subtype => `${subtype.deviceType?.name || 'Asset'} - ${subtype.name}`)
                    .join(', ');
            }
            return updated;
        }));
    }, [teamTypes, deviceSubTypes]);

    const saveRequirement = useCallback(async (requirement) => {
        setSavingRequirementId(requirement.id);
        setError('');
        try {
            const updated = await fetchWithAuth(endpoint(`team-asset-requirements/${requirement.id}/`), {
                method: 'PATCH',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json', 'X-CSRFToken': getCSRFToken() },
                body: JSON.stringify(requirementPayload(requirement))
            }).then(responseData);
            setRequirements(current => current.map(currentRequirement => currentRequirement.id === requirement.id
                ? requirementToRow(updated || requirement, teamTypes, deviceSubTypes)
                : currentRequirement));
        } catch (response) {
            await handleWriteError(response);
        } finally {
            setSavingRequirementId(null);
        }
    }, [teamTypes, deviceSubTypes]);

    const deleteRequirement = useCallback(async id => {
        setError('');
        try {
            await fetchWithAuth(endpoint(`team-asset-requirements/${id}/`), {
                method: 'DELETE',
                credentials: 'include',
                headers: { 'X-CSRFToken': getCSRFToken() }
            }).then(responseData);
            setRequirements(current => current.filter(requirement => requirement.id !== id));
        } catch (response) {
            await handleWriteError(response);
        }
    }, []);

    const addRequirement = async () => {
        setAdding(true);
        setError('');
        try {
            const created = await fetchWithAuth(endpoint('team-asset-requirements/'), {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json', 'X-CSRFToken': getCSRFToken() },
                body: JSON.stringify(requirementPayload(newRequirement))
            }).then(responseData);
            setRequirements(current => [...current, requirementToRow(created, teamTypes, deviceSubTypes)]);
            setNewRequirement(emptyRequirement);
        } catch (response) {
            await handleWriteError(response);
        } finally {
            setAdding(false);
        }
    };

    const subtypeOptions = useMemo(() => deviceSubTypes.map(subtype => ({
        key: subtype.id,
        value: subtype.id,
        text: `${subtype.deviceType?.name || 'Asset'} - ${subtype.name}`
    })), [deviceSubTypes]);

    const columns = useMemo(() => {
        const result = [
            {
                Header: 'Team Type',
                accessor: 'teamTypeName',
                Cell: ({ row }) => readOnly ? row.original.teamTypeName : (
                    <Dropdown
                        data-testid="requirement-team-type"
                        fluid
                        search
                        selection
                        options={teamTypes.map(type => ({ key: type.id, value: type.id, text: type.name }))}
                        value={row.original.teamTypeId}
                        onChange={(event, data) => updateRequirementField(row.original.id, 'teamTypeId', data.value)}
                    />
                )
            },
            {
                Header: 'Label',
                accessor: 'label',
                Cell: ({ row }) => readOnly ? row.original.label : (
                    <Input
                        data-testid="requirement-label"
                        fluid
                        value={row.original.label}
                        onChange={(event, data) => updateRequirementField(row.original.id, 'label', data.value)}
                    />
                )
            },
            {
                Header: 'Quantity',
                accessor: 'quantity',
                Cell: ({ row }) => readOnly ? row.original.quantity : (
                    <Input
                        min="1"
                        type="number"
                        value={row.original.quantity}
                        onChange={(event, data) => updateRequirementField(row.original.id, 'quantity', data.value)}
                    />
                )
            },
            {
                Header: 'Device Subtypes',
                accessor: 'deviceSubTypeNames',
                Cell: ({ row }) => readOnly ? row.original.deviceSubTypeNames : (
                    <Dropdown
                        data-testid="requirement-subtypes"
                        fluid
                        multiple
                        search
                        selection
                        options={subtypeOptions}
                        value={row.original.deviceSubTypeIds}
                        onChange={(event, data) => updateRequirementField(row.original.id, 'deviceSubTypeIds', data.value)}
                    />
                )
            },
            {
                Header: 'Sort Order',
                accessor: 'sortOrder',
                Cell: ({ row }) => readOnly ? row.original.sortOrder : (
                    <Input
                        min="0"
                        type="number"
                        value={row.original.sortOrder}
                        onChange={(event, data) => updateRequirementField(row.original.id, 'sortOrder', data.value)}
                    />
                )
            }
        ];

        if (!readOnly) {
            result.push({
                Header: 'Actions',
                id: 'actions',
                disableFilters: true,
                disableSortBy: true,
                Cell: ({ row }) => (
                    <Button.Group size="small">
                        <Button
                            primary
                            icon="save"
                            aria-label={`Save ${row.original.label}`}
                            loading={savingRequirementId === row.original.id}
                            onClick={() => saveRequirement(row.original)}
                        />
                        <Button
                            negative
                            icon="trash"
                            aria-label={`Delete ${row.original.label}`}
                            onClick={() => deleteRequirement(row.original.id)}
                        />
                    </Button.Group>
                )
            });
        }

        return result;
    }, [readOnly, teamTypes, subtypeOptions, updateRequirementField, savingRequirementId, saveRequirement, deleteRequirement]);

    return (
        <LoadingWrapper loading={!loaded} text="Loading asset requirements">
            <div style={{ padding: '1.5rem', maxWidth: '1200px', margin: '0 auto' }}>
                <Header as="h2">Team Asset Requirements</Header>
                {readOnly && <Message info>Team asset requirements are read-only.</Message>}
                {error && <Message negative onDismiss={() => setError('')}>{error}</Message>}
                {requirements.length === 0 ? (
                    <Message info content="No team asset requirements found." />
                ) : (
                    <ReactTable
                        className="team-asset-requirements-table react-table-mobile-compact"
                        data={requirements}
                        columns={columns}
                    />
                )}
                {!readOnly && (
                    <Form style={{ marginTop: '1.5rem' }}>
                        <Header as="h3">Add Requirement</Header>
                        <Form.Group widths="equal">
                            <Form.Field>
                                <label>Team Type</label>
                                <Dropdown
                                    data-testid="requirement-team-type"
                                    fluid
                                    search
                                    selection
                                    options={teamTypes.map(type => ({ key: type.id, value: type.id, text: type.name }))}
                                    value={newRequirement.teamTypeId}
                                    onChange={(event, data) => setNewRequirement(current => ({ ...current, teamTypeId: data.value }))}
                                />
                            </Form.Field>
                            <Form.Input
                                data-testid="requirement-label"
                                label="Label"
                                value={newRequirement.label}
                                onChange={(event, data) => setNewRequirement(current => ({ ...current, label: data.value }))}
                            />
                            <Form.Input
                                label="Quantity"
                                min="1"
                                type="number"
                                value={newRequirement.quantity}
                                onChange={(event, data) => setNewRequirement(current => ({ ...current, quantity: data.value }))}
                            />
                            <Form.Field>
                                <label>Device Subtypes</label>
                                <Dropdown
                                    data-testid="requirement-subtypes"
                                    fluid
                                    multiple
                                    search
                                    selection
                                    options={subtypeOptions}
                                    value={newRequirement.deviceSubTypeIds}
                                    onChange={(event, data) => setNewRequirement(current => ({ ...current, deviceSubTypeIds: data.value }))}
                                />
                            </Form.Field>
                            <Form.Input
                                label="Sort Order"
                                min="0"
                                type="number"
                                value={newRequirement.sortOrder}
                                onChange={(event, data) => setNewRequirement(current => ({ ...current, sortOrder: data.value }))}
                            />
                        </Form.Group>
                        <Button
                            primary
                            icon
                            labelPosition="left"
                            loading={adding}
                            disabled={!newRequirement.teamTypeId || !newRequirement.label.trim() || newRequirement.quantity < 1}
                            onClick={addRequirement}
                        >
                            <Icon name="plus" /> Add Requirement
                        </Button>
                    </Form>
                )}
            </div>
        </LoadingWrapper>
    );
};